import { CONTEXT_FIELDS, type ContextInput } from '@innspel/core';
import type { HandoffResult } from '@innspel/core/handoff';
import { useEffect, useState } from 'react';
import { Ikon } from '../Ikon.tsx';
import { useInnspel } from '../InnspelProvider.tsx';
import { Banner, Btn, H, Hint } from './primitiver.tsx';
import { tekst } from './tekster.ts';

export interface HandoffSurfaceProps {
  /** The id from `/h/<id>` — the last path segment of the URL the TV's QR code pointed to. */
  handoffId: string;
  /** Fetches the session: `fetchHandoff` from `@innspel/core/handoff`, bound to the host's base URL and key. */
  load: () => Promise<HandoffResult>;
  tenantName?: string;
  /**
   * «Logg inn hos …» (log in with the tenant) for an anonymous client. Gets the lines the submitter
   * removed, so the host can keep them across the redirect. Left out = no login button.
   */
  onLogIn?: (removed: string[]) => void;
  /** Lines removed before a login redirect, restored by the host. */
  initialRemoved?: string[];
}

/**
 * 4 — the handoff from a big screen, reached through the QR link `/h/<id>` (S1.3). The TV's
 * context is shown line by line, each removable (DD-05) except `platform`: the contract requires
 * it, it must be the TV's, and the widget's own context list never offers it either. Two choices
 * for an anonymous client — send anonymously (safe on a family TV) or log in with the tenant —
 * and one for a client with identity. Both open the dialog with `handoff`, so the submission
 * carries `handoffId` and exactly the lines that were kept.
 *
 * The same component in the portal and in `mount(el, { handoffId })` — one code base, two outputs.
 */
export function HandoffSurface({ handoffId, load, tenantName, onLogIn, initialRemoved = [] }: HandoffSurfaceProps) {
  const { client, open } = useInnspel();
  const [res, setRes] = useState<HandoffResult | null>(null);
  const [fjernede, setFjernede] = useState<string[]>(initialRemoved);
  const tittel = tenantName ? tekst.tvTittel(tenantName) : tekst.dialogSteg.valg!;

  useEffect(() => {
    let aktiv = true;
    setRes(null);
    void load().then((r) => aktiv && setRes(r));
    return () => {
      aktiv = false;
    };
    // `load` er bundet til id-en; en ny funksjon per render skal ikke hente på nytt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoffId]);

  if (!res) {
    return <H nivaa={1} sub={tekst.tvHenter}>{tittel}</H>;
  }

  if (res.status !== 'ok') {
    return (
      <>
        <H nivaa={1} sub={tekst.tvGyldighet}>
          {res.status === 'utlopt' ? tekst.tvUtloptTittel : tekst.tvFeilTittel}
        </H>
        <Banner ikon="time-outline" tone="warning" live testId="tv-utlopt">
          {res.status === 'utlopt' ? tekst.tvUtlopt : tekst.tvFeil}
        </Banner>
        <Btn variant="secondary" ikon="chatbubble-outline" testId="send-uten-tv" label={tekst.tvUtenKontekst} hint={tekst.tvUtenKontekstHint} onClick={() => void open()} />
      </>
    );
  }

  // Bare den lukkede lista (K5): TV-ens `sdkVersion` er repoets ord, ikke innsenderens (DD-50).
  const kontekst = res.handoff.context as unknown as Record<string, unknown>;
  const linjer = CONTEXT_FIELDS.filter((k) => typeof kontekst[k] === 'string' && kontekst[k] && !fjernede.includes(k)).map(
    (k) => [k, tekst.tvEtiketter[k] ?? k, menneskeord(k, kontekst[k] as string)] as const,
  );
  const beholdt = Object.fromEntries(linjer.map(([k]) => [k, kontekst[k]])) as Partial<ContextInput>;
  // TV-ens koblingsnøkler: én linje, fjernbar som resten. Fjernet = ikke sendt. Verdiene er
  // ugjennomsiktige og vises bare som tekst (React escaper), aldri som lenke eller markup.
  const nøkler = fjernede.includes(KOBLINGSNOKLER) ? [] : (res.handoff.correlation ?? []);
  const send = () => void open({ handoff: { id: handoffId, context: beholdt, ...(nøkler.length ? { correlation: nøkler } : {}) } });

  return (
    <>
      <H nivaa={1} sub={tekst.tvUnder}>{tittel}</H>
      <div role="group" aria-label={tekst.tvGruppe} className="innspel-stabel" data-gap="sm">
        <div className="innspel-tv-linje">
          <Ikon navn="tv-outline" />
          {linjer.map(([, , v]) => v).join(' · ')}
        </div>
        <ul className="innspel-liste" style={{ gap: 0 }}>
          {linjer.map(([felt, etikett, verdi]) => (
            <li key={felt} className="innspel-kontekst-linje">
              <span className="innspel-kontekst-nokkel">{etikett}</span>
              <span className="innspel-kontekst-verdi">{verdi}</span>
              {felt === 'platform' ? null : (
                <button type="button" className="innspel-ikonknapp" aria-label={tekst.kontekstFjernA11y(etikett)} onClick={() => setFjernede((f) => [...f, felt])}>
                  <Ikon navn="close-outline" />
                  {tekst.kontekstFjern}
                </button>
              )}
            </li>
          ))}
          {nøkler.length ? (
            <li className="innspel-kontekst-linje">
              <span className="innspel-kontekst-nokkel">{tekst.tvKoblingsnokler(nøkler.length)}</span>
              <span className="innspel-kontekst-verdi">{nøkler.map((c) => `${c.kind} ${c.value}`).join(' · ')}</span>
              <button type="button" className="innspel-ikonknapp" data-testid="fjern-koblingsnokler" aria-label={tekst.tvFjernKoblingsnokler} onClick={() => setFjernede((f) => [...f, KOBLINGSNOKLER])}>
                <Ikon navn="close-outline" />
                {tekst.kontekstFjern}
              </button>
            </li>
          ) : null}
        </ul>
        <Hint>{tekst.tvAldri}</Hint>
      </div>
      {client.isAnonymous ? (
        <>
          <Btn tall ikon="chatbubble-outline" testId="tv-send-anonymt" label={tekst.tvSendAnonymt} hint={tekst.tvSendAnonymtHint} onClick={send} />
          {onLogIn && tenantName ? (
            <Btn tall variant="secondary" ikon="key-outline" testId="tv-logg-inn" label={tekst.tvLoggInn(tenantName)} hint={tekst.tvLoggInnHint(tenantName)} onClick={() => onLogIn(fjernede)} />
          ) : null}
        </>
      ) : (
        <Btn tall ikon="chatbubble-outline" testId="tv-send" label={tekst.tvSendInnlogget} {...(tenantName ? { hint: tekst.tvSendInnloggetHint(tenantName) } : {})} onClick={send} />
      )}
    </>
  );
}

/**
 * DD-50 presisert 2026-09-20: kontraktens enumverdi er REPOETS ord, aldri innsenderens.
 * `platform: 'tvos'` sto ordrett som enhetens navn på denne skjermen — «tvos» er ikke et
 * ord noen sier. Verdien oversettes; alt annet vises som det kom fra TV-en.
 */
/** The key for the correlation line among the removed lines — also what survives a login redirect. */
const KOBLINGSNOKLER = 'correlation';

const PLATTFORM: Record<string, string> = {
  tvos: 'TV',
  androidtv: 'TV',
  tizen: 'TV',
  webos: 'TV',
  android: 'Android',
  ios: 'iPhone',
  web: 'Nettleser',
};

function menneskeord(felt: string, verdi: string): string {
  return felt === 'platform' ? (PLATTFORM[verdi] ?? verdi) : verdi;
}
